// r24 Wave 5 — In-body JSON-LD emitter (BreadcrumbList + Service graph)
// Google parses inline <script type="application/ld+json"> in body.
// Consolidates schema for all 274+ NAATI leaf pages via 3 renderers.

const SITE = "https://nyclanguageinstitute.com";
const ORG_ID = `${SITE}/#organization`;

export interface Crumb {
  name: string;
  path: string; // relative
}

export interface NaatiSchemaGraphProps {
  locale: "th" | "en";
  path: string; // canonical page path e.g. /translate/naati/faq/xxx
  title: string;
  description: string;
  crumbs: Crumb[];
  areaName?: string; // city / province / country name
  areaRegion?: string;
  areaCountry?: string; // ISO code
}

export default function NaatiSchemaGraph({
  locale, path, title, description, crumbs, areaName, areaRegion, areaCountry,
}: NaatiSchemaGraphProps) {
  const url = `${SITE}${path}`;
  const lang = locale === "th" ? "th-TH" : "en";

  const graph: any[] = [
    {
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: `${SITE}${c.path}`,
      })),
    },
    {
      "@type": "Service",
      "@id": `${url}#service`,
      name: title,
      description,
      inLanguage: lang,
      url,
      serviceType: "NAATI Certified Translation",
      provider: { "@id": ORG_ID },
      ...(areaName
        ? {
            areaServed: {
              "@type": "AdministrativeArea",
              name: areaName,
              ...(areaRegion || areaCountry
                ? {
                    address: {
                      "@type": "PostalAddress",
                      ...(areaRegion ? { addressRegion: areaRegion } : {}),
                      ...(areaCountry ? { addressCountry: areaCountry } : {}),
                    },
                  }
                : {}),
            },
          }
        : {}),
    },
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      inLanguage: lang,
      isPartOf: { "@id": `${SITE}/#website` },
      breadcrumb: { "@id": `${url}#breadcrumb` },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["[data-speakable]", ".direct-answer", "h1"],
      },
    },
  ];

  const json = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: json }}
    />
  );
}
